import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import type { ClientChartBundle } from '../pages/clients/data/clientChartBundle';
import { fetchMedplumClientChartBundle } from '../lib/medplumChartBundle';
import {
  chartScopeFromRoleAndPractitioner,
  getAuthorizedEpisodeIdsForChart,
} from '../lib/rbac/eocChartScope';
import { useSupervisedPractitionerIds } from './useSupervisedPractitionerIds';
import type { ResourceScope } from '../lib/rbac/scopeAccess';
import { getViewScope } from '../lib/rbac/scopeAccess';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';
import { useMedplumSubscriptionRefresh } from './useMedplumSubscriptionRefresh';
import {
  buildPatientAppointmentSubscriptionCriteria,
  buildPatientDocumentReferenceSubscriptionCriteria,
  buildPatientQuestionnaireResponseSubscriptionCriteria,
  buildPatientTaskSubscriptionCriteria,
} from '../lib/fhirRealtimeCriteria';

/**
 * Resource types loaded by fetchMedplumClientChartBundle.
 * Invalidated before every fetch so re-fetches always hit the server.
 */
const CHART_BUNDLE_RESOURCE_TYPES = [
  'EpisodeOfCare',
  'Appointment',
  'Encounter',
  'QuestionnaireResponse',
  'Observation',
  'Task',
  'DocumentReference',
] as const;

/** Drill-in authorization for medical record rows (appointment page, EoC detail, note modal). */
export interface EpisodeChartInteraction {
  authorizedEpisodeIds: ReadonlySet<string>;
  viewScope: ResourceScope;
  patientEpisodeCount: number;
}

/**
 * Loads Medplum chart data for one patient. Lists include all episode-scoped rows; use
 * `episodeInteraction` with `isEpisodeScopedRowVisible` to gate navigation and modals.
 */
export function useEpisodeScopedClientChartBundle(
  patientId: string | undefined,
): {
  bundle: ClientChartBundle | undefined;
  loading: boolean;
  error: Error | null;
  episodeInteraction: EpisodeChartInteraction | undefined;
  refetch: () => void;
} {
  const medplum = useMedplum();
  const { role } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const { ids: supervisedPractitionerIds, loading: superviseesLoading } = useSupervisedPractitionerIds();
  const viewScope = getViewScope(role);
  const needsPractitionerForScope = viewScope === 'OWN';
  const [bundle, setBundle] = useState<ClientChartBundle | undefined>();
  const [loading, setLoading] = useState(!!patientId);
  const [error, setError] = useState<Error | null>(null);
  const [fetchVersion, setFetchVersion] = useState(0);
  /** True once this patient has a loaded bundle — used to avoid full-page loading on WS refetch. */
  const hasBundleRef = useRef(false);

  const refetch = useCallback(() => setFetchVersion((v) => v + 1), []);

  useEffect(() => {
    hasBundleRef.current = false;
    setBundle(undefined);
    setLoading(!!patientId);
    setError(null);
  }, [patientId]);

  // Re-fetch when the provider returns to this browser tab (catches external
  // scheduling changes, bot outputs, etc. that happened while away).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  // WebSocket subscriptions — trigger a full bundle refetch when Appointment
  // or Task resources for this patient change on the server (real-time updates).
  const appointmentCriteria = useMemo(
    () => (patientId ? buildPatientAppointmentSubscriptionCriteria(patientId) : undefined),
    [patientId],
  );
  const taskCriteria = useMemo(
    () => (patientId ? buildPatientTaskSubscriptionCriteria(patientId) : undefined),
    [patientId],
  );
  const questionnaireResponseCriteria = useMemo(
    () => (patientId ? buildPatientQuestionnaireResponseSubscriptionCriteria(patientId) : undefined),
    [patientId],
  );
  const documentReferenceCriteria = useMemo(
    () => (patientId ? buildPatientDocumentReferenceSubscriptionCriteria(patientId) : undefined),
    [patientId],
  );
  useMedplumSubscriptionRefresh(appointmentCriteria, refetch, {
    debugLabel: '[Chart][WS][Appointment]',
  });
  useMedplumSubscriptionRefresh(taskCriteria, refetch, {
    debugLabel: '[Chart][WS][Task]',
  });
  useMedplumSubscriptionRefresh(questionnaireResponseCriteria, refetch, {
    debugLabel: '[Chart][WS][QuestionnaireResponse]',
  });
  useMedplumSubscriptionRefresh(documentReferenceCriteria, refetch, {
    debugLabel: '[Chart][WS][DocumentReference]',
  });

  useEffect(() => {
    if (!patientId) {
      setBundle(undefined);
      setError(null);
      return;
    }
    if (needsPractitionerForScope && practitionerLoading) {
      setLoading(true);
      return;
    }
    const resolvedPatientId = patientId;
    let cancelled = false;
    const isInitialLoad = !hasBundleRef.current;
    async function run() {
      if (isInitialLoad) {
        setLoading(true);
      }
      try {
        // Invalidate Medplum client-side search cache for all resource types
        // used in the bundle so every fetch (including explicit refetch()) hits
        // the server rather than stale in-memory results.
        for (const rt of CHART_BUNDLE_RESOURCE_TYPES) {
          medplum.invalidateSearches(rt);
        }
        const raw = await fetchMedplumClientChartBundle(medplum, resolvedPatientId);
        if (cancelled) {
          return;
        }
        if (!cancelled) {
          setBundle(raw);
          hasBundleRef.current = true;
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled && isInitialLoad) {
          setLoading(false);
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, role, practitioner?.id, practitionerLoading, needsPractitionerForScope, fetchVersion]);

  const episodeInteraction = useMemo((): EpisodeChartInteraction | undefined => {
    if (!bundle) {
      return undefined;
    }
    const ctx = chartScopeFromRoleAndPractitioner(
      role,
      practitioner?.id,
      supervisedPractitionerIds,
    );
    const authorized = getAuthorizedEpisodeIdsForChart(
      bundle.episodes.map((e) => ({
        id: e.id,
        careManagerPractitionerId: e.careManagerPractitionerId,
      })),
      ctx,
    );
    return {
      authorizedEpisodeIds: new Set(authorized),
      viewScope: getViewScope(role),
      patientEpisodeCount: bundle.episodes.length,
    };
  }, [bundle, role, practitioner?.id, supervisedPractitionerIds]);

  return { bundle, loading: loading || superviseesLoading, error, episodeInteraction, refetch };
}
